import { useState, useEffect, useMemo, useCallback } from 'react';
import { DiscoverHeader } from './components/DiscoverHeader';
import { DiscoverFilters } from './components/DiscoverFilters';
import { ProductCard } from './components/ProductCard';
import { TrendingSidebar } from './components/TrendingSidebar';
import { CheckedBar } from './components/CheckedBar';
import { SubmitProductModal } from './components/SubmitProductModal';
import { ImportCsvModal } from './components/ImportCsvModal';
import { supabase } from '../../lib/supabase';
import { useCheckedProducts } from '../../hooks/useCheckedProducts';

export default function DiscoverPage() {
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedPricing, setSelectedPricing] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('newest');
  const [isSubmitModalOpen, setIsSubmitModalOpen] = useState(false);
  const [isCsvImportOpen, setIsCsvImportOpen] = useState(false);
  const [products, setProducts] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  // Bulk delete selection state
  const [bulkSelectMode, setBulkSelectMode] = useState(false);
  const [selectedForDelete, setSelectedForDelete] = useState<Set<string>>(new Set());
  const [isBulkDeleting, setIsBulkDeleting] = useState(false);

  // Use the shared hook instead of local state
  const { checkedIds, toggleCheck, clearAll, count } = useCheckedProducts();

  const fetchProducts = async () => {
    try {
      setIsLoading(true);
      const { data, error } = await supabase
        .from('products')
        .select('*')
        .eq('status', 'approved')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setProducts(data || []);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchProducts();
  }, []);

  const handleDeleteProduct = async (productId: string) => {
    if (!window.confirm('Are you sure you want to delete this product?')) return;
    try {
      const { error } = await supabase.from('products').delete().eq('id', productId);
      if (error) throw error;
      setProducts(prev => prev.filter(p => p.id !== productId));
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  // Calculate filtered products first
  const filteredProducts = useMemo(() => {
    let filtered = [...products];

    // Filter by search query
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(p =>
        p.name?.toLowerCase().includes(query) ||
        p.tagline?.toLowerCase().includes(query) ||
        p.description?.toLowerCase().includes(query)
      );
    }

    // Filter by category
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(p => p.category === selectedCategory);
    }

    // Filter by pricing
    if (selectedPricing !== 'all') {
      filtered = filtered.filter(p => p.pricing?.toLowerCase() === selectedPricing.toLowerCase());
    }

    // Sort products
    switch (sortBy) {
      case 'trending':
        filtered.sort((a, b) => (b.upvotes || 0) - (a.upvotes || 0));
        break;
      case 'rating':
        filtered.sort((a, b) => (b.rating || 0) - (a.rating || 0));
        break;
      case 'users':
        filtered.sort((a, b) => (b.users || 0) - (a.users || 0));
        break;
      case 'newest':
      default:
        filtered.sort(
          (a, b) =>
            new Date(b.created_at).getTime() - new Date(a.created_at).getTime()
        );
        break;
    }

    return filtered;
  }, [products, searchQuery, selectedCategory, selectedPricing, sortBy]);

  // Bulk‑select helpers
  const toggleSelectForDelete = useCallback((id: string) => {
    setSelectedForDelete(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }, []);

  const selectAllVisible = useCallback(() => {
    setSelectedForDelete(new Set(filteredProducts.map(p => p.id)));
  }, [filteredProducts]);

  const deselectAll = useCallback(() => {
    setSelectedForDelete(new Set());
  }, []);

  const handleBulkDelete = async () => {
    if (selectedForDelete.size === 0) return;
    const ids = Array.from(selectedForDelete);
    setIsBulkDeleting(true);
    try {
      const { error } = await supabase.from('products').delete().in('id', ids);
      if (error) throw error;
      setProducts(prev => prev.filter(p => !selectedForDelete.has(p.id)));
      setSelectedForDelete(new Set());
      setBulkSelectMode(false);
    } catch (error) {
      console.error('Error bulk deleting:', error);
    } finally {
      setIsBulkDeleting(false);
    }
  };

  const exitBulkMode = useCallback(() => {
    setBulkSelectMode(false);
    setSelectedForDelete(new Set());
  }, []);

  return (
    <div className="min-h-screen bg-gray-50">
      <DiscoverHeader
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        onSubmitClick={() => setIsSubmitModalOpen(true)}
        onCsvImportClick={() => setIsCsvImportOpen(true)}
        onDeduplicateComplete={fetchProducts}
        bulkSelectMode={bulkSelectMode}
        onToggleBulkSelect={() => {
          if (bulkSelectMode) exitBulkMode();
          else setBulkSelectMode(true);
        }}
      />

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="flex gap-8">
          <div className="flex-1">
            <DiscoverFilters
              searchQuery={searchQuery}
              onSearchChange={setSearchQuery}
              activeCategory={selectedCategory}
              onCategoryChange={setSelectedCategory}
              pricingFilter={selectedPricing}
              onPricingChange={setSelectedPricing}
              sortBy={sortBy}
              onSortChange={setSortBy}
            />

            {isLoading ? (
              <div className="text-center py-12">
                <div className="inline-block w-8 h-8 border-4 border-teal-500 border-t-transparent rounded-full animate-spin"></div>
                <p className="mt-4 text-gray-600">Loading products...</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-8">
                {filteredProducts.map(product => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    checked={checkedIds.includes(product.id)}
                    onToggleCheck={() => toggleCheck(product.id)}
                    onDelete={() => handleDeleteProduct(product.id)}
                    bulkSelectMode={bulkSelectMode}
                    bulkSelected={selectedForDelete.has(product.id)}
                    onToggleBulkSelect={() => toggleSelectForDelete(product.id)}
                  />
                ))}
              </div>
            )}

            {!isLoading && filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <i className="ri-search-line text-6xl text-gray-300"></i>
                <p className="mt-4 text-gray-600">No products found matching your criteria</p>
              </div>
            )}
          </div>

          <TrendingSidebar products={products} />
        </div>
      </div>

      {bulkSelectMode && (
        <CheckedBar
          count={selectedForDelete.size}
          onClear={deselectAll}
          mode="bulk-delete"
          totalVisible={filteredProducts.length}
          onSelectAll={selectAllVisible}
          onBulkDelete={handleBulkDelete}
          isBulkDeleting={isBulkDeleting}
          onExitBulkMode={exitBulkMode}
        />
      )}

      {!bulkSelectMode && count > 0 && (
        <CheckedBar count={count} onClear={clearAll} mode="add-products" />
      )}

      <SubmitProductModal
        isOpen={isSubmitModalOpen}
        onClose={() => setIsSubmitModalOpen(false)}
      />

      <ImportCsvModal
        isOpen={isCsvImportOpen}
        onClose={() => setIsCsvImportOpen(false)}
        onImportComplete={fetchProducts}
      />
    </div>
  );
}
